import { FirebaseError, initializeApp } from "firebase/app"
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type AuthProvider,
} from "firebase/auth"
import { queryClient } from "./react-query"
import { useTempStore } from "./store"
import { usePermStore } from "./permStore"
import { loginTokenLocalStorageKey, workosLocalStorageKey, apiBaseUrl } from "./utils"
import posthog from "posthog-js"
import { captureError, trackEvent } from "./log"

const firebaseConfig = {
  apiKey: "AIzaSyAeuWD0qtLnRPzbNwZsjgrdqMn9P4kKJFI",
  authDomain: "simulead-508bd.firebaseapp.com",
  projectId: "simulead-508bd",
  storageBucket: "simulead-508bd.appspot.com",
  messagingSenderId: "265295624208",
  appId: "1:265295624208:web:fff466c94b7b8f7eb2f295",
  measurementId: "G-1S5HZ66DKY",
}

const app = initializeApp(firebaseConfig)

export const auth = getAuth(app)

// Firebase error codes raised when the user dismisses the OAuth popup or
// re-triggers it while one is already open — expected behaviour, not app errors
const dismissedPopupErrorCodes = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
])

// Shared popup sign-in wrapper: swallows benign user-dismissal rejections
// (previously surfaced in Sentry as unhandled rejections — SIMULEAD-FRONTEND-XG)
// and rethrows genuine failures (popup blocked, network, unauthorized domain)
// after reporting them, so callers can show the user feedback
async function signInWithProviderPopup({
  provider,
  providerName,
}: {
  provider: AuthProvider
  providerName: "google" | "microsoft"
}) {
  try {
    await signInWithPopup(auth, provider)
    // Successful sign-in is handled by the auth state listener, so there is
    // nothing further to do here
  } catch (error) {
    if (
      error instanceof FirebaseError &&
      dismissedPopupErrorCodes.has(error.code)
    ) {
      // User closed/re-opened the popup themselves — track for funnel
      // visibility but don't treat as an error. Fire-and-forget with a catch
      // so analytics failures can't become new unhandled rejections.
      trackEvent({
        eventName: "dismissed login popup",
        properties: { provider: providerName, errorCode: error.code },
      }).catch(() => {})
    } else {
      // Genuine failure — report it, then rethrow so the login UI can show feedback
      captureError(error, {
        tags: { context: "login", provider: providerName },
      })
      throw error
    }
  }
}

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider()
  await signInWithProviderPopup({ provider, providerName: "google" })
}

export async function loginWithMicrosoft() {
  const provider = new OAuthProvider("microsoft.com")
  provider.setCustomParameters({
    // "select_account" shows Microsoft's account picker so users with multiple
    // MS accounts choose the right one, without forcing a full re-consent each login.
    prompt: "select_account",
  })
  await signInWithProviderPopup({ provider, providerName: "microsoft" })
}

export function loginWithApple() {}

// eslint-disable-next-line
;(window as any).getToken = async () => {
  const token = await auth.currentUser?.getIdToken()
  console.log(token)
}

export async function logout() {
  // Capture token before any async operations
  const workosToken = localStorage.getItem(workosLocalStorageKey)

  // Run Firebase signOut and WorkOS session invalidation in parallel
  const results = await Promise.allSettled([
    signOut(auth),
    // Invalidate WorkOS session on the backend if user was logged in via SSO
    // Token sent in headers (same pattern as authenticated requests)
    workosToken
      ? fetch(`${apiBaseUrl}/workos/profile-sessions`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "token-type": "workos-profile-session",
            token: workosToken,
          },
          signal: AbortSignal.timeout(5000),
        })
      : Promise.resolve(),
  ])

  // Report any failures to Sentry/PostHog for observability
  const [firebaseResult, workosResult] = results
  if (firebaseResult?.status === "rejected") {
    captureError(firebaseResult.reason, {
      tags: { context: "logout", provider: "firebase" },
    })
  }
  if (workosToken && workosResult?.status === "rejected") {
    captureError(workosResult.reason, {
      tags: { context: "logout", provider: "workos" },
    })
  }
  // Check HTTP status for WorkOS response (fetch only rejects on network failure)
  if (
    workosToken &&
    workosResult?.status === "fulfilled" &&
    workosResult.value instanceof Response &&
    !workosResult.value.ok
  ) {
    captureError(new Error("WorkOS session invalidation failed"), {
      tags: { context: "logout", provider: "workos" },
      extra: { status: workosResult.value.status },
    })
  }

  localStorage.removeItem(workosLocalStorageKey)
  localStorage.removeItem(loginTokenLocalStorageKey)

  // Reset PostHog identity on logout
  posthog.reset()

  // Clearup
  queryClient.clear()
  const { setStore: setAppStore } = useTempStore.getState()
  setAppStore({
    authProfile: undefined,
    user: undefined,
  })
  const { setPermStore } = usePermStore.getState()
  setPermStore({
    userId: undefined,
    emulateUserId: undefined,
  })
}
